<template>
    <span
        v-if="badgeLabels.length > 0"
        class="enum-cell enum-cell--badges"
        :data-test-id="dataTestId"
    >
        <SearchBadge
            v-for="(badgeLabel, index) in badgeLabels"
            :key="index"
            :data-test-id="`${dataTestId}-item-${index}`"
            :label="badgeLabel"
        />
    </span>
    <span
        v-else
        class="enum-cell"
        :data-test-id="dataTestId"
    >
        <img
            v-if="option?.icon"
            alt=""
            class="enum-cell__icon"
            :data-test-id="`${dataTestId}-icon`"
            :src="option.icon"
        />
        <span
            class="enum-cell__label"
            :data-test-id="`${dataTestId}-label`"
            >{{ label }}</span
        >
    </span>
</template>

<script>
import EMPTY_LABEL from './empty-label'
import SearchBadge from './SearchBadge.vue'

export default {
    name: 'EnumCell',

    components: {
        SearchBadge,
    },

    props: {
        dataTestId: {
            type: String,
            required: true,
        },
        options: {
            type: Array,
            required: true,
        },
        // An array for a multi-valued column, such as a user's roles.
        value: {
            type: [Array, Boolean, String],
            required: false,
            default: null,
        },
    },

    computed: {
        EMPTY_LABEL: () => EMPTY_LABEL,

        isMultiValued() {
            return Array.isArray(this.value)
        },

        // A multi-valued cell renders one badge per value it recognizes. A
        // value with no matching option is skipped rather than shown as an
        // empty badge, so a cell that recognizes none of its values renders
        // no badges at all and falls back to the empty label below.
        badgeLabels() {
            if (!this.isMultiValued) {
                return []
            }

            return this.value.map((value) => this.optionFor(value)?.label).filter(Boolean)
        },

        // Only a single-valued cell shows an icon: a row of them alongside a
        // list of badges reads as noise, and no multi-valued column has one.
        option() {
            return this.isMultiValued ? null : this.optionFor(this.value)
        },

        // The single value's label. A multi-valued cell only reaches this as
        // its empty fallback, since anything it can label it renders as a
        // badge instead.
        label() {
            if (this.isMultiValued) {
                return EMPTY_LABEL
            }

            return this.optionFor(this.value)?.label ?? EMPTY_LABEL
        },
    },

    methods: {
        optionFor(value) {
            return this.options.find((option) => option.value === value) ?? null
        },
    },
}
</script>

<style lang="scss" scoped>
.enum-cell {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    overflow: hidden;
}

// Badges wrap rather than overflow, and the row sizes to its content so a
// short list is not stretched across the column. min-width: 0 lets each
// badge shrink below its natural width so its label can truncate.
.enum-cell--badges {
    flex-wrap: wrap;
    width: auto;
    min-width: 0;
    max-width: 100%;
}

.enum-cell__icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
}

.enum-cell__label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
